import { inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, defineProperty as _defineProperty, assertThisInitialized as _assertThisInitialized, typeof as _typeof, objectSpread as _objectSpread, createClass as _createClass, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withContext } from '@piggybank/core';
import { Consumer } from './context.js';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import { getIn } from 'formik';
import FieldProvider from './FieldProvider.js';
import identity from 'lodash/fp/identity';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/fp/isEqual';
import mapValues from 'lodash/fp/mapValues';

var FormikAdapter = /*#__PURE__*/function (_Component) {
  _inherits(FormikAdapter, _Component);

  function FormikAdapter(props) {
    var _this;

    _classCallCheck(this, FormikAdapter);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(FormikAdapter).call(this, props)); // If this field is already touched when it first renders,
    // 'untouch' it so that errors are hidden

    _defineProperty(_assertThisInitialized(_this), "setValue", function (_ref) {
      var value = _ref.value;

      _this.setState({
        value: value
      });

      _this.setFieldValue(_this.props.field.name, value);
    });

    _defineProperty(_assertThisInitialized(_this), "setFieldValue", debounce(function () {
      var _this$props$form;

      return (_this$props$form = _this.props.form).setFieldValue.apply(_this$props$form, arguments);
    }, typeof _this.props.debounceWait !== 'undefined' ? _this.props.debounceWait : getIn(_this.props, 'form.status.debounceWait'), {
      leading: true
    }));

    _defineProperty(_assertThisInitialized(_this), "handleChange", function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      if (_this.props.onChange) {
        var _this$props;

        (_this$props = _this.props).onChange.apply(_this$props, [_this.setValue].concat(args));
      } else {
        var _this2;

        (_this2 = _this).setValue.apply(_this2, args);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleBlur", function (e) {
      var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          isGroup = _ref2.isGroup;

      if (_this.props.context && _this.props.context.onBlur) {
        _this.props.context.onBlur(e, {
          isGroup: true
        });
      }

      if (!isGroup) {
        if (_this.props.onBlur) {
          _this.props.onBlur(_this.setTouchedWhenNotEmpty, {
            event: e
          });
        } else {
          _this.setTouchedWhenNotEmpty();
        }
      }

      _this.timeout = setTimeout(function () {
        var error = _this.getError();

        var touched = _this.getTouched();

        var invalidGroup = _this.getInvalidGroup();

        var invalid = _this.getInvalid(error, touched, invalidGroup);

        var fieldErrorAnnouncement = invalid ? error : '';

        if (getIn(_this.props, 'form.status.fieldErrorAnnouncement') !== fieldErrorAnnouncement) {
          _this.props.form.setStatus({
            fieldErrorAnnouncement: fieldErrorAnnouncement
          });
        }
      }, 10);
    });

    _defineProperty(_assertThisInitialized(_this), "setTouchedWhenNotEmpty", function () {
      _this.setFieldValue.flush();

      if (!_this.getEmpty()) {
        _this.props.form.setFieldTouched(_this.props.field.name);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "unwrapValue", function (value) {
      // For the special case of { raw, value } such as TelephoneInput
      if (_typeof(value) === 'object' && typeof value.value !== 'undefined') {
        return value.value;
      }

      return value;
    });

    _defineProperty(_assertThisInitialized(_this), "getEmpty", function () {
      return isEmpty(_this.unwrapValue(_this.state.value));
    });

    _defineProperty(_assertThisInitialized(_this), "getError", function () {
      var error = getIn(_this.props.form.errors, _this.props.field.name);

      var unwrappedError = _this.unwrapValue(error);

      return typeof unwrappedError === 'string' ? unwrappedError : undefined;
    });

    _defineProperty(_assertThisInitialized(_this), "getTouched", function () {
      var touched = getIn(_this.props.form.touched, _this.props.field.name);

      if (_typeof(touched) === 'object') {
        // Must be a group of fields, check they're all touched
        var fieldsMappedToFalse = mapValues(function () {
          return false;
        }, getIn(_this.props.form.initialValues, _this.props.field.name));
        return Object.values(_objectSpread({}, fieldsMappedToFalse, touched)).every(identity);
      }

      return touched;
    });

    _defineProperty(_assertThisInitialized(_this), "getInvalid", function (error, touched, invalidGroup) {
      var hasError = typeof error === 'string';
      var submitted = _this.props.form.submitCount > 0;

      var empty = _this.getEmpty();

      if (touched && (submitted || !empty) && (hasError || invalidGroup)) {
        return true;
      }

      return false;
    });

    _defineProperty(_assertThisInitialized(_this), "getInvalidGroup", function () {
      if (_this.props.context) {
        var _this$props$context = _this.props.context,
            touched = _this$props$context.touched,
            error = _this$props$context.error,
            name = _this$props$context.name;
        return touched && typeof error === 'string' && name;
      }

      return false;
    });

    if (_this.getTouched()) {
      _this.props.form.setFieldTouched(_this.props.field.name, false);
    }

    _this.state = {
      value: props.field.value,
      lastFormikFieldValue: props.field.value
    };
    return _this;
  }

  _createClass(FormikAdapter, [{
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      clearTimeout(this.timeout);
      this.setFieldValue.flush();
    }
  }, {
    key: "render",
    value: function render() {
      var _omit = omit(['form', 'context', 'onChange', 'onBlur', 'meta', 'debounceWait', 'invalidateNestedFields'], this.props),
          name = _omit.field.name,
          rest = _objectWithoutProperties(_omit, ["field"]);

      var error = this.getError();
      var touched = this.getTouched();
      var invalidGroup = this.getInvalidGroup();
      var invalid = this.getInvalid(error, touched, invalidGroup); // If the containing group is invalid, create an additional string for aria-describedby
      // using the name of the group to reconstruct the group's FieldFeedback id.
      // Note: This assumes the group's Fieldset has a FieldFeedback.

      var extraDescriber = invalid && invalidGroup && "".concat(invalidGroup, "-error-message") || null;
      return React.createElement(FieldProvider, _extends({
        name: name,
        value: this.state.value,
        onChange: this.handleChange,
        onBlur: this.handleBlur,
        error: error,
        touched: touched,
        invalid: invalid,
        extraDescriber: extraDescriber
      }, rest));
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      if (!isEqual(props.field.value, state.lastFormikFieldValue)) {
        return {
          value: props.field.value,
          lastFormikFieldValue: props.field.value
        };
      }

      return null;
    }
  }]);

  return FormikAdapter;
}(Component);

FormikAdapter.propTypes = process.env.NODE_ENV !== "production" ? {
  debounceWait: PropTypes.number,
  invalidateNestedFields: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    onBlur: PropTypes.func
  }),
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    setStatus: PropTypes.func,
    initialValues: PropTypes.object,
    touched: PropTypes.object,
    errors: PropTypes.object,
    status: PropTypes.object,
    submitCount: PropTypes.number
  }),
  context: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    touched: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func
} : {};
var FormikAdapter$1 = withContext(Consumer, function (value) {
  return {
    context: value
  };
})(FormikAdapter);
export default FormikAdapter$1;
export { FormikAdapter };